import type { ReactElement } from 'react'

import type { Poem } from '@maria-cavalheiro-website/shared-types'
import { findCollection } from '@maria-cavalheiro-website/shared-types/config'

import { StanzaLines } from '../poems/stanza-lines.tsx'
import { PoemPlate } from '../scenes/poem-plate.tsx'

export interface EbookPageViewProps {
  readonly poem: Poem
  /** Heading id, so the dialog's page can be named without colliding. */
  readonly titleId: string
}

/**
 * One page of the book. Pure presenter — no state, no effects, no data access.
 *
 * NOT `PoemArticle`, AND THE REASON IS AN ID. That component names its heading
 * `poema-<slug>` and points the article's `aria-labelledby` at it. Open the reader
 * on a poem route and the same poem is on the page behind the dialog, so reusing
 * it would put two elements with the same id in one document — which axe reports
 * as `duplicate-id-aria`, and which makes the `aria-labelledby` genuinely
 * ambiguous rather than merely untidy. The id arrives as a prop instead.
 *
 * NO STANZA FIGURES, EITHER. The poem route gives every stanza its own picture
 * across an alternating spread; a page that has to fit a phone in landscape has
 * room for the verse and the emblem that marks which poem it is. The stanzas are
 * the same markup as the route's, through `StanzaLines`, so a verse break cannot
 * come out differently in the two places.
 *
 * AN `<h2>`, FIXED, NOT A `headingLevel` PROP. Both routes that can open the book
 * already own their document's `<h1>` — the home hero and the poem title — so a
 * page inside the dialog is a level below on either one, and there is no third
 * caller for the prop to serve.
 */
export function EbookPageView({ poem, titleId }: EbookPageViewProps): ReactElement {
  const collection = findCollection(poem.collection)
  const legend = [collection?.label, poem.theme].filter(Boolean).join(' · ')

  return (
    <article className="ebook__page" aria-labelledby={titleId}>
      <PoemPlate name={poem.scene} />

      <p className="ebook__legend">{legend}</p>

      <h2 className="ebook__title" id={titleId}>
        {poem.title}
      </h2>

      <div className="ebook__verse">
        {poem.stanzas.map((stanza, index) => (
          <p className="ebook__stanza" key={`${poem.slug}-estrofe-${String(index)}`}>
            <StanzaLines stanza={stanza} />
          </p>
        ))}
      </div>
    </article>
  )
}
